// Human units on the settings screens (spec/13 §13, 2026-10-07) — display only. A value is stored, sent and audited
// exactly as the registry declares it (seconds, basis points, minor units); the screen shows and takes it in the unit a
// café owner thinks in, and converts back exactly on save. No float is involved anywhere: a typed amount is split at
// its decimal point and scaled with integers, so every conversion round-trips (`__tests__/settings-units.test.ts`).
//
//   duration     seconds shown as minutes when they are whole minutes (grace 300 → 5 minutes), otherwise kept in
//                seconds; minutes, hours, days, months and years keep their unit and gain its word
//   percentBps   basis points as a percentage with up to two decimals (1000 → 10, 1250 → 12.5)
//   count        "3 a week", "3 a month"
//   celsius      "90 °C"

import type { DisplayUnit } from './layout';

export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years';

/** How many of the stored unit make one of the shown unit: seconds → minutes is 60. */
const SECONDS: Partial<Record<DurationUnit, number>> = { seconds: 1, minutes: 60, hours: 3600 };

/** The units a stored duration can be shown in, largest first: a value in seconds can read as minutes. */
export function durationUnitsFor(display: Extract<DisplayUnit, { kind: 'duration' }>): readonly DurationUnit[] {
  if (display.stored !== 'seconds' || display.shown === undefined) return [display.stored];
  return display.shown === 'hours' ? ['hours', 'minutes', 'seconds'] : ['minutes', 'seconds'];
}

/** The factor from `unit` to the stored unit (both are seconds-based, or the unit is the stored one). */
export function factorOf(unit: DurationUnit, stored: DurationUnit): number {
  if (unit === stored) return 1;
  const from = SECONDS[unit];
  const to = SECONDS[stored];
  if (from === undefined || to === undefined) throw new Error(`No conversion from ${unit} to ${stored}.`);
  return from / to;
}

/** The largest shown unit the stored value is a whole number of: 300 s → minutes, 90 s → seconds. */
export function bestDurationUnit(value: number, display: Extract<DisplayUnit, { kind: 'duration' }>): DurationUnit {
  const units = durationUnitsFor(display);
  return units.find((unit) => Number.isSafeInteger(value) && value % factorOf(unit, display.stored) === 0) ?? display.stored;
}

/** A stored duration in a shown unit: always a whole number, by `bestDurationUnit`'s choice. */
export function durationToShown(value: number, unit: DurationUnit, stored: DurationUnit): number {
  return value / factorOf(unit, stored);
}

export type ParseResult = { readonly ok: true; readonly value: number } | { readonly ok: false; readonly reason: 'whole' | 'invalid' | 'fraction' };

/** A typed whole number in a shown unit → the stored integer. "5" minutes → 300 seconds. */
export function durationFromShown(text: string, unit: DurationUnit, stored: DurationUnit): ParseResult {
  const trimmed = text.trim().replaceAll(',', '');
  if (!/^\d+$/.test(trimmed)) return { ok: false, reason: /^\d*\.\d+$/.test(trimmed) ? 'whole' : 'invalid' };
  const value = Number(trimmed) * factorOf(unit, stored);
  return Number.isSafeInteger(value) ? { ok: true, value } : { ok: false, reason: 'invalid' };
}

/** Basis points → the percentage as typed: 1000 → "10", 1250 → "12.5", 5 → "0.05". */
export function bpsToPercentText(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const fraction = String(Math.abs(bps % 100)).padStart(2, '0').replace(/0+$/, '');
  return fraction === '' ? String(whole) : `${whole}.${fraction}`;
}

/** A typed percentage → basis points, exactly: "12.5" → 1250, "10%" → 1000. Up to two decimals. */
export function percentTextToBps(text: string): ParseResult {
  const trimmed = text.trim().replace(/%$/, '').trim();
  const match = /^(\d{1,3})(?:\.(\d+))?$/.exec(trimmed);
  if (match === null) return { ok: false, reason: 'invalid' };
  const fraction = match[2] ?? '';
  if (fraction.length > 2) return { ok: false, reason: 'fraction' };
  return { ok: true, value: Number(match[1]) * 100 + Number(fraction.padEnd(2, '0') || '0') };
}

/** The plural-aware words a unit reads with, from the console's copy. */
export type UnitWords = (key: string, options: Record<string, unknown>) => string;

/** A stored value in words, in its display unit: 300 (seconds) → "5 minutes"; 1000 (bps) → "10%". */
export function formatWithUnit(value: unknown, display: DisplayUnit, t: UnitWords): string | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  switch (display.kind) {
    case 'duration': {
      const unit = bestDurationUnit(value, display);
      return t(`settings.unit.${unit}`, { count: durationToShown(value, unit, display.stored) });
    }
    case 'percentBps':
      return t('settings.unit.percent', { value: bpsToPercentText(value) });
    case 'count':
      return t(display.per === 'week' ? 'settings.unit.perWeek' : 'settings.unit.perMonth', { count: value });
    case 'celsius':
      return t('settings.unit.celsius', { value });
  }
}
